import {Controller} from '@hotwired/stimulus'
import Sortable from 'sortablejs'

// Duration to suppress morphing refreshes after a drag completes (ms).
// This prevents a race condition where a Pusher-driven morph arrives before
// the status-change API call finishes, causing the project card to appear
// in both the old and new columns (GF-1909).
const DRAG_COOLDOWN_MS = 2000
const KANBAN_STATUS_CLASS_PREFIX = 'kanban-status-'

function updateDraggedCardStatusClass(item, status) {
  if (!item)
    return

  item.classList.forEach((className) => {
    if (className.startsWith(KANBAN_STATUS_CLASS_PREFIX))
      item.classList.remove(className)
  })

  item.classList.add(`${KANBAN_STATUS_CLASS_PREFIX}${status}`)
}

export default class extends Controller {
  connect() {
    this.sortables = []
    this.dragCooldownTimer = null
    const groupName = this.element.dataset.kanbanGroup || 'kanban'
    const allowReorder = this.element.dataset.allowReorder === 'true'
    this.element.querySelectorAll('.cards').forEach((col) => {
      const sortable = new Sortable(col, {
        group: groupName,
        // sort controls within-list reordering; cross-list drag is governed by `group`
        // and stays enabled even when sort: false.
        sort: allowReorder,
        animation: 150,
        // Keep fallback mode for native HTML5 drag-preview snapback artifacts.
        // Cooldown below still guards against stale pusher updates right after drop.
        forceFallback: true,
        fallbackOnBody: true,
        handle: '.js-drag-handle',
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        onEnd: (evt) => {
          const item = evt.item
          const projectId = item.dataset.projectId
          const newStatus = evt.to.dataset.status
          const organizationId = this.element.dataset.organizationId
          const sortContext
            = this.element.dataset.sortContext || 'kanban_person'
          const shouldRefreshForEmptyList
            = this.shouldRefreshForEmptyList(newStatus)

          updateDraggedCardStatusClass(item, newStatus)

          // Set a cooldown flag on the containing turbo frame to suppress morphing
          // refreshes while the API call is in flight. This prevents a race where
          // a morph with stale data duplicates the dragged card.
          this.startDragCooldown()

          const url = `/organizations/${organizationId}/work_items/update_kanban_items`

          const payload = {
            work_item_dom_id: projectId,
            organization_id: organizationId,
            sort_context: sortContext,
            project: {
              status: newStatus,
            },
          }

          if (allowReorder) {
            const orderedDomIds = Array.from(
              this.element.querySelectorAll('.js-drag-handle'),
            ).map((el) => el.dataset.projectId)
            payload.ordered_dom_ids = orderedDomIds
          }

          fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
                .content,
            },
            body: JSON.stringify(payload),
          }).then((response) => {
            if (!response.ok) {
              console.error('Failed to update') // eslint-disable-line no-console
            } else {
              console.log('Updated successfully') // eslint-disable-line no-console
              if (shouldRefreshForEmptyList) {
                Glassfrog.hotwire.refreshTurboFrame(
                  'page-frame',
                  document.location,
                )
              }
            }
          })
        },
      })
      this.sortables.push(sortable)
    })
  }

  // Set a data attribute on the turbo frame to signal pusherStreams.js that a
  // drag just completed and morphing refreshes should be suppressed.
  startDragCooldown() {
    const contentFrame = document.getElementById('content-frame')
    const nearestFrame = this.element.closest('turbo-frame')
    const framesToMark = [contentFrame, nearestFrame].filter(
      (frame, index, all) => frame && all.indexOf(frame) === index,
    )

    document.body?.setAttribute('data-drag-cooldown', 'true')
    framesToMark.forEach((frame) => frame.setAttribute('data-drag-cooldown', 'true'))

    if (this.dragCooldownTimer)
      clearTimeout(this.dragCooldownTimer)
    this.dragCooldownTimer = setTimeout(() => {
      document.body?.removeAttribute('data-drag-cooldown')
      framesToMark.forEach((frame) => frame.removeAttribute('data-drag-cooldown'))
      this.dragCooldownTimer = null
    }, DRAG_COOLDOWN_MS)
  }

  // When the last non-archived card is dragged to Archived, reload the page frame
  // so Rails can render the canonical empty state.
  shouldRefreshForEmptyList(newStatus) {
    if (newStatus !== 'archived' || !this.isWorkspaceTab())
      return false

    const remainingNonArchivedCards = document.querySelectorAll(
      '[data-controller~="kanban-sortable"] .cards:not([data-status="archived"]) .js-drag-handle',
    ).length

    return remainingNonArchivedCards === 0
  }

  isWorkspaceTab() {
    const tab = this.element.dataset.tab || ''
    return tab.startsWith('workspace_')
  }

  disconnect() {
    if (this.sortables)
      this.sortables.forEach((sortable) => sortable.destroy())

    if (this.dragCooldownTimer)
      clearTimeout(this.dragCooldownTimer)
  }
}
